import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { CreateClientFlow } from '@/components/admin/CreateClientFlow';
 import { Plus, Search, ArrowUpDown, TrendingUp, TrendingDown, Minus, Mail } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
 import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
 import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface OrgData {
  org_id: string;
  org_name: string;
  status: string;
  scored_7d: number;
  scored_30d: number;
  wau_7d: number;
  fsr_lift_30d: number;
  pct_users_improving: number;
  last_activity: string | null;
  active_users: number;
  total_users: number;
}

type SortField = 'org_name' | 'scored_7d' | 'scored_30d' | 'wau_7d' | 'fsr_lift_30d' | 'pct_users_improving' | 'last_activity';
type SortDirection = 'asc' | 'desc';

 interface InviteRequest {
   id: string;
   email: string;
   created_at: string;
   status: string;
 }
 
export default function AdminHome() {
  const navigate = useNavigate();
  const [orgs, setOrgs] = useState<OrgData[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState<SortField>('scored_7d');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [createFlowOpen, setCreateFlowOpen] = useState(false);
 
   const [inviteRequests, setInviteRequests] = useState<InviteRequest[]>([]);
   const [inviteLoading, setInviteLoading] = useState(true);

  const fetchOrgs = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc('get_all_orgs_admin');
      if (error) throw error;
      setOrgs(data || []);
    } catch (error) {
      console.error('Error fetching orgs:', error);
    } finally {
      setLoading(false);
    }
  };

   const fetchInviteRequests = async () => {
     setInviteLoading(true);
     try {
       const { data, error } = await supabase
         .from('invite_requests')
         .select('*')
         .order('created_at', { ascending: false });
       if (error) throw error;
       setInviteRequests(data || []);
     } catch (error) {
       console.error('Error fetching invite requests:', error);
     } finally {
       setInviteLoading(false);
     }
   };
 
   const updateRequestStatus = async (id: string, status: string) => {
     try {
       const { error } = await supabase
         .from('invite_requests')
         .update({ status })
         .eq('id', id);
       if (error) throw error;
       setInviteRequests(prev => 
         prev.map(req => req.id === id ? { ...req, status } : req)
       );
     } catch (error) {
       console.error('Error updating status:', error);
     }
   };
 
  useEffect(() => {
    fetchOrgs();
     fetchInviteRequests();
  }, []);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const sortedOrgs = [...orgs]
    .filter((org) => org.org_name.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      
      if (sortField === 'last_activity') {
        aVal = aVal ? new Date(aVal as string).getTime() : 0;
        bVal = bVal ? new Date(bVal as string).getTime() : 0;
      }
      
      if (typeof aVal === 'string') {
        return sortDirection === 'asc' 
          ? aVal.localeCompare(bVal as string)
          : (bVal as string).localeCompare(aVal);
      }
      
      return sortDirection === 'asc' 
        ? (aVal as number) - (bVal as number)
        : (bVal as number) - (aVal as number);
    });

  const SortHeader = ({ field, children }: { field: SortField; children: React.ReactNode }) => (
    <TableHead 
      className="cursor-pointer hover:bg-muted/50 transition-colors"
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-1">
        {children}
        <ArrowUpDown className={`h-3 w-3 ${sortField === field ? 'opacity-100' : 'opacity-30'}`} />
      </div>
    </TableHead>
  );

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-500/10 text-green-700 border-green-500/30';
      case 'pilot': return 'bg-blue-500/10 text-blue-700 border-blue-500/30';
      case 'paused': return 'bg-gray-500/10 text-gray-700 border-gray-500/30';
      default: return '';
    }
  };

  const getLiftIndicator = (lift: number) => {
    if (lift > 0) return <TrendingUp className="h-4 w-4 text-green-500" />;
    if (lift < 0) return <TrendingDown className="h-4 w-4 text-red-500" />;
    return <Minus className="h-4 w-4 text-muted-foreground" />;
  };

   const pendingInvites = inviteRequests.filter(r => r.status === 'pending').length;
 
  return (
    <div className="space-y-6">
       <Tabs defaultValue="organizations" className="w-full">
         <TabsList>
           <TabsTrigger value="organizations">Organizations</TabsTrigger>
           <TabsTrigger value="invites" className="relative">
             Invite Requests
             {pendingInvites > 0 && (
               <span className="ml-2 bg-primary text-primary-foreground text-xs px-1.5 py-0.5 rounded-full">
                 {pendingInvites}
               </span>
             )}
           </TabsTrigger>
         </TabsList>
 
         <TabsContent value="organizations" className="space-y-6">
           {/* Header */}
           <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
             <div>
               <h1 className="text-2xl font-bold">Organizations</h1>
               <p className="text-muted-foreground">Manage client organizations and monitor adoption</p>
             </div>
             <Button className="w-full md:w-auto" onClick={() => setCreateFlowOpen(true)}>
               <Plus className="h-4 w-4 mr-2" />
               Create Client
             </Button>
           </div>
 
           {/* Search */}
           <div className="relative max-w-sm">
             <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
             <Input
               placeholder="Search organizations..."
               value={search}
               onChange={(e) => setSearch(e.target.value)}
               className="pl-9"
             />
           </div>
 
           {/* Table */}
           <div className="border rounded-lg">
             <Table>
               <TableHeader>
                 <TableRow>
                   <SortHeader field="org_name">Organization</SortHeader>
                   <TableHead>Status</TableHead>
                   <SortHeader field="scored_7d"><span className="w-full text-center">Submissions (7d)</span></SortHeader>
                   <SortHeader field="scored_30d"><span className="w-full text-center">Submissions (30d)</span></SortHeader>
                   <SortHeader field="wau_7d">WAU</SortHeader>
                   <SortHeader field="fsr_lift_30d">FSR Lift (30d)</SortHeader>
                   <SortHeader field="pct_users_improving">% Improving</SortHeader>
                   <SortHeader field="last_activity">Last Activity</SortHeader>
                   <TableHead>Seats</TableHead>
                 </TableRow>
               </TableHeader>
               <TableBody>
                 {loading ? (
                   <TableRow>
                     <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                       Loading...
                     </TableCell>
                   </TableRow>
                 ) : sortedOrgs.length === 0 ? (
                   <TableRow>
                     <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                       {search ? 'No organizations found' : 'No organizations yet'}
                     </TableCell>
                   </TableRow>
                 ) : (
                   sortedOrgs.map((org) => (
                     <TableRow 
                       key={org.org_id}
                       className="cursor-pointer hover:bg-muted/50"
                       onClick={() => navigate(`/admin/org/${org.org_id}`)}
                     >
                       <TableCell className="font-medium">{org.org_name}</TableCell>
                       <TableCell>
                         <Badge variant="outline" className={getStatusColor(org.status)}>
                           {org.status}
                         </Badge>
                       </TableCell>
                       <TableCell className="font-semibold text-center">{org.scored_7d}</TableCell>
                       <TableCell className="text-center">{org.scored_30d}</TableCell>
                       <TableCell>{org.wau_7d}</TableCell>
                       <TableCell>
                         <div className="flex items-center gap-1">
                           {getLiftIndicator(org.fsr_lift_30d)}
                           <span className={org.fsr_lift_30d > 0 ? 'text-green-600' : org.fsr_lift_30d < 0 ? 'text-red-600' : ''}>
                             {org.fsr_lift_30d > 0 ? '+' : ''}{org.fsr_lift_30d}
                           </span>
                         </div>
                       </TableCell>
                       <TableCell>
                         {org.pct_users_improving > 0 ? `${org.pct_users_improving}%` : '-'}
                       </TableCell>
                       <TableCell className="text-muted-foreground">
                         {org.last_activity 
                           ? formatDistanceToNow(new Date(org.last_activity), { addSuffix: true })
                           : 'Never'
                         }
                       </TableCell>
                       <TableCell>
                         <span className="text-muted-foreground">
                           {org.active_users} / {org.total_users}
                         </span>
                       </TableCell>
                     </TableRow>
                   ))
                 )}
               </TableBody>
             </Table>
           </div>
         </TabsContent>
 
         <TabsContent value="invites" className="space-y-6">
           <div>
             <h1 className="text-2xl font-bold">Invite Requests</h1>
             <p className="text-muted-foreground">Manage incoming demo and access requests</p>
           </div>
 
           <div className="border rounded-lg">
             <Table>
               <TableHeader>
                 <TableRow>
                   <TableHead>Email</TableHead>
                   <TableHead>Requested</TableHead>
                   <TableHead>Status</TableHead>
                 </TableRow>
               </TableHeader>
               <TableBody>
                 {inviteLoading ? (
                   <TableRow>
                     <TableCell colSpan={3} className="text-center py-8 text-muted-foreground">
                       Loading...
                     </TableCell>
                   </TableRow>
                 ) : inviteRequests.length === 0 ? (
                   <TableRow>
                     <TableCell colSpan={3} className="text-center py-8 text-muted-foreground">
                       <Mail className="h-8 w-8 mx-auto mb-2 opacity-30" />
                       No invite requests yet
                     </TableCell>
                   </TableRow>
                 ) : (
                   inviteRequests.map((request) => (
                     <TableRow key={request.id}>
                       <TableCell className="font-medium">{request.email}</TableCell>
                       <TableCell className="text-muted-foreground">
                         {formatDistanceToNow(new Date(request.created_at), { addSuffix: true })}
                       </TableCell>
                       <TableCell>
                         <Select
                           value={request.status}
                           onValueChange={(value) => updateRequestStatus(request.id, value)}
                         >
                           <SelectTrigger className="w-32">
                             <SelectValue />
                           </SelectTrigger>
                           <SelectContent>
                             <SelectItem value="pending">Pending</SelectItem>
                             <SelectItem value="contacted">Contacted</SelectItem>
                             <SelectItem value="converted">Converted</SelectItem>
                           </SelectContent>
                         </Select>
                       </TableCell>
                     </TableRow>
                   ))
                 )}
               </TableBody>
             </Table>
           </div>
         </TabsContent>
       </Tabs>

      {/* Create Client Flow Modal */}
      <CreateClientFlow
        open={createFlowOpen}
        onOpenChange={setCreateFlowOpen}
        onComplete={fetchOrgs}
      />
    </div>
  );
}
